import {
  mapRunningTime,
  mapRunningTimeDto,
  mapRunningTimes,
  mapRunningTimeStoppageDto
} from '@domain/shared'
import {
  DateRange,
  RunningTime,
  RunningTimeDto,
  RunningTimeStoppage,
  StoppageDto
} from '@hconnect/common/types'
import {SxProps, Theme} from '@mui/material'
import moment, {Moment} from 'moment-timezone'
import {ReactNode} from 'react'

import {MIN_BAR_WIDTH_IN_PX, RESIZE_HANDLE_WIDTH_IN_PX} from '../consts'
import {type BarForChartProps} from '../types'

type TimeSegment = {
  start: Moment
  end: Moment
}

export function getRunningTimeIndex(
  currentUndefinedStoppages?: RunningTime[],
  item?: Partial<StoppageDto>
) {
  if (!currentUndefinedStoppages) return undefined
  const index = currentUndefinedStoppages.findIndex((rt) =>
    rt.beginTechnical?.isSame(item?.stoppageStart)
  )
  return index >= 0 ? index : undefined
}

export const getStoppageEnd = (stoppageEnd?: Moment, runningTimeEnd?: Moment) => {
  if (stoppageEnd && !runningTimeEnd) {
    return stoppageEnd
  }
  if (stoppageEnd && runningTimeEnd && stoppageEnd.isBefore(runningTimeEnd)) {
    return stoppageEnd
  }
  return runningTimeEnd
}

export const getStoppageDate = (stoppage: RunningTimeStoppage, rt: RunningTime) => {
  const stoppageStart = moment.utc(stoppage.start)
  const runningTimeStart = moment.utc(rt.begin)

  const from = stoppageStart.isBefore(runningTimeStart) ? rt.begin : stoppage.start
  const to = getStoppageEnd(stoppage.end, rt.end)

  return {from, to}
}

export const getStoppagesInDateRange = (
  dateRange: DateRange,
  stoppages: RunningTimeStoppage[] = []
) => {
  return stoppages.filter((stoppage) => {
    const startsBeforeOrAtRangeEnd = stoppage.start.isSameOrBefore(dateRange.endDate)
    const endsAfterOrAtRangeStart = stoppage.end
      ? stoppage.end.isSameOrAfter(dateRange.startDate)
      : true
    return startsBeforeOrAtRangeEnd && endsAfterOrAtRangeStart
  })
}
export const includeGapsInStoppages = (
  runningTime: RunningTime,
  dateRange: DateRange,
  timezone: string
): RunningTimeStoppage[] => {
  if (!runningTime.stoppages?.length) return runningTime.stoppages ?? []
  const lastStoppage = runningTime.stoppages[runningTime.stoppages.length - 1]

  const stoppagesWithMissingPieces: RunningTimeStoppage[] = []

  if (
    runningTime.beginTechnical &&
    !runningTime.stoppages[0].start.isSame(runningTime.beginTechnical)
  ) {
    stoppagesWithMissingPieces.push({
      start: runningTime.beginTechnical,
      end: runningTime.stoppages[0].start,
      stoppageKind: lastStoppage.stoppageKind
    })
  }

  runningTime.stoppages.forEach((stoppage, index) => {
    stoppagesWithMissingPieces.push(stoppage)
    const nextStoppage: RunningTimeStoppage | undefined = runningTime.stoppages?.[index + 1]
    if (nextStoppage && !moment(stoppage.end).isSame(moment(nextStoppage.start))) {
      stoppagesWithMissingPieces.push({
        start: stoppage.end || moment().tz(timezone),
        end: nextStoppage.start,
        stoppageKind: stoppage.stoppageKind
      })
    }
  })

  if (lastStoppage.end?.isBefore(runningTime.endTechnical)) {
    stoppagesWithMissingPieces.push({
      start: lastStoppage.end || moment().tz(timezone),
      ...(runningTime.endTechnical && {end: runningTime.endTechnical}),
      stoppageKind: lastStoppage.stoppageKind
    })
  }

  return getStoppagesInDateRange(dateRange, stoppagesWithMissingPieces)
}

export function includeGapsInRunningTimes(
  timeRange: DateRange,
  data: RunningTimeDto[],
  timezone: string
) {
  const runningTimesWithGaps: RunningTimeDto[] = []
  if (data.length === 0) {
    return []
  }
  if (moment(timeRange.startDate).isBefore(moment(data[0].begin))) {
    const beginElement: RunningTimeDto = {
      begin: timeRange.startDate.toISOString(),
      end: data[0].begin,
      runningTimeType: undefined
    }

    runningTimesWithGaps.push(beginElement)
  }

  data.forEach((rt, index) => {
    const stoppagesWithGapsArray = includeGapsInStoppages(
      mapRunningTime(rt, timezone),
      timeRange,
      timezone
    )
    runningTimesWithGaps.push({
      ...rt,
      begin: moment(rt.begin).isBefore(timeRange.startDate)
        ? timeRange.startDate.toISOString()
        : rt.begin,
      end: moment(rt.end).isAfter(timeRange.endDate) ? timeRange.endDate.toISOString() : rt.end,
      stoppages: stoppagesWithGapsArray.map(mapRunningTimeStoppageDto)
    })
    if (data[index + 1] && !moment(rt.end).isSame(moment(data[index + 1].begin))) {
      runningTimesWithGaps.push({
        begin: rt.end,
        end: data[index + 1].begin,
        runningTimeType: undefined
      })
    }
  })

  if (moment(timeRange.endDate).isAfter(moment(data[data.length - 1].end))) {
    const endElement: RunningTimeDto = {
      begin: data[data.length - 1].end,
      end: timeRange.endDate.toISOString(),
      runningTimeType: undefined
    }

    runningTimesWithGaps.push(endElement)
  }

  return runningTimesWithGaps
}

export function getBarPercentage(
  timeStart: Moment | undefined,
  timeEnd: Moment | undefined,
  totalRunningTimeMs: number
) {
  if (!timeStart || !timeEnd || timeStart === timeEnd) {
    return '0%'
  }

  const diffInMs = timeEnd.diff(timeStart, 'milliseconds')
  const percent = (diffInMs / totalRunningTimeMs) * 100

  return `${percent}%`
}

export function calculateMinPercent(
  numBars: number,
  containerWidth: number,
  minBarPx: number = MIN_BAR_WIDTH_IN_PX,
  resizeHandleWidthPx: number = RESIZE_HANDLE_WIDTH_IN_PX
): number {
  if (!containerWidth || numBars === 0) return 0

  const totalResizeWidth = (numBars - 1) * resizeHandleWidthPx
  const availableBarWidth = containerWidth - totalResizeWidth

  const percentPerPixel = 100 / availableBarWidth
  return percentPerPixel * minBarPx
}

export function getMaxEditableBarCount(
  graphWidth: number,
  minBarWidthPx: number = MIN_BAR_WIDTH_IN_PX,
  resizeHandleWidthPx: number = RESIZE_HANDLE_WIDTH_IN_PX
): number {
  const maxBars = Math.floor(
    (graphWidth + resizeHandleWidthPx) / (minBarWidthPx + resizeHandleWidthPx)
  )
  return Math.max(maxBars, 0)
}

export function getAdjustedBarPercentages(
  segments: TimeSegment[],
  totalDurationMs: number,
  minPercent = 1
): string[] {
  if (segments.length === 0 || totalDurationMs === 0) return []

  const rawDurations = segments.map((seg) => seg.end.diff(seg.start, 'milliseconds'))
  const rawPercents = rawDurations.map((d) => (d / totalDurationMs) * 100)

  const tooSmallIndices = rawPercents
    .map((p, i) => (p < minPercent ? i : null))
    .filter((i): i is number => i !== null)

  const remainingIndices = rawPercents
    .map((_, i) => (tooSmallIndices.includes(i) ? null : i))
    .filter((i): i is number => i !== null)

  const remainingTotalDuration = rawDurations
    .filter((_, idx) => remainingIndices.includes(idx))
    .reduce((sum, d) => sum + d, 0)

  const remainingPercentTotal = 100 - tooSmallIndices.length * minPercent

  return rawPercents.map((_, i) => {
    if (tooSmallIndices.includes(i)) return `${minPercent}%`
    const originalFraction = rawDurations[i] / remainingTotalDuration
    return `${originalFraction * remainingPercentTotal}%`
  })
}

export const getRunningTimesWithGap = (
  runningTimes: RunningTimeDto[],
  timeRange: DateRange,
  timezone: string
) => {
  const filteredData = runningTimes
    .filter((rt) => rt.begin !== rt.end)
    .filter((rt) => {
      const begin = moment.tz(rt.begin, timezone)
      const end = moment.tz(rt.end, timezone)
      return end.isAfter(timeRange.startDate) && begin.isBefore(timeRange.endDate)
    })

  const runningTimesWithGaps = includeGapsInRunningTimes(timeRange, filteredData, timezone)
  return mapRunningTimes(runningTimesWithGaps, timezone)
}

export const getBarPercentages = (
  totalRunningTimeMs: number,
  data: RunningTime[],
  barWidth?: number,
  useTechnicalDates?: boolean
) => {
  const segments: TimeSegment[] = data.map((rt) => ({
    start: (useTechnicalDates ? rt.beginTechnical : rt.begin) || moment(),
    end: (useTechnicalDates ? rt.endTechnical : rt.end) || moment()
  }))
  const minPercent = barWidth ? calculateMinPercent(data.length, barWidth) : 1
  return getAdjustedBarPercentages(segments, totalRunningTimeMs, minPercent)
}

export const getBarForChartProps = ({
  timezone,
  isEditMode,
  barPercentages,
  rt,
  rtDto,
  aboveBarComponent,
  handleRunningClick,
  totalRunningTimeMs,
  sx,
  data,
  index
}: {
  timezone: string
  isEditMode?: boolean
  barPercentages?: string[]
  rt: RunningTime
  rtDto: RunningTimeDto
  aboveBarComponent?: (rt: RunningTimeDto) => ReactNode
  totalRunningTimeMs: number
  data: RunningTime[]
  index: number
  handleRunningClick?: (rt: RunningTimeDto) => void | Promise<void>
  sx?: SxProps<Theme>
}): BarForChartProps => ({
  timezone: timezone,
  percentage: barPercentages?.[index] || getBarPercentage(rt.begin, rt.end, totalRunningTimeMs),
  runningTime: rt,
  sx: {
    ...sx,
    ...(isEditMode && rt.stoppages?.length ? {cursor: 'default'} : {})
  },
  aboveBarComponent: index === data.length - 1 ? aboveBarComponent?.(rtDto) : undefined,
  isEditMode,
  handleRunningClick: handleRunningClick
    ? () => handleRunningClick(mapRunningTimeDto(rt))
    : undefined
})
